// src/pages/dashboard/directorproducer/ManageApplicationList.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Spinner, Alert, Card, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaTrash } from 'react-icons/fa';

import { getAppliedRoleListForDrCr } from '../../services/api'; 
import MetaTags from '../../components/MetaTags'; 
import DataTable from '../../components/DataTable'; // ✅ Import reusable Table
import './ManageApplicationsPage.css'; 

const ManageApplicationList = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    const navigate = useNavigate();

    // ✅ LOAD & MAP DATA
    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        
        try {
            const response = await getAppliedRoleListForDrCr();
            const rawData = response.data.data || [];

            // ✅ MAP NEW API RESPONSE
            const transformedData = rawData.map(item => {
                let month = 'N/A';
                let date = 'N/A';

                // Parse slot_datetime if available
                if (item.slot_datetime) {
                    const d = new Date(item.slot_datetime);
                    if (!isNaN(d)) {
                        month = `${d.getFullYear()}${(d.getMonth() + 1).toString().padStart(2, '0')}`; // eg. 202603
                        date = `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`; // eg. 29/03/2026
                    }
                }

                return {
                    id: item.roleApplicationId, // unique key
                    candidateName: item.candidateName || item.talent_name || 'Unknown',
                    roleName: item.role_name || item.c_role_name || 'N/A',
                    projectName: item.project_name || 'N/A',
                    // Clean up VIDEO_UPLOAD to "Video Upload"
                    auditionType: item.booking_type ? item.booking_type.replace('_', ' ') : 'N/A',
                    verifyStatus: item.booking_status || 'Pending',
                    status: item.result || 'Pending',
                    month: month,
                    date: date,
                };
            });

            setData(transformedData);
        } catch (err) {
            console.error(err);
            setError('Failed to load application data.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { 
        loadData(); 
    }, [loadData]);

    const handleViewApp = (roleApplicationId) => {
        navigate(`/dashboard/manage-applications/${roleApplicationId}`);
    };

    // ✅ DEFINE COLUMNS FOR TANSTACK TABLE
    const columns = useMemo(() => [
        {
            accessorKey: 'index',
            header: '#',
            cell: (info) => <span className="opacity-50 text-white">{info.row.index + 1}</span>,
            enableSorting: false,
        },
        {
            accessorKey: 'month',
            header: 'Month',
            cell: (info) => <span className="text-white opacity-75">{info.getValue()}</span>,
        },
        {
            accessorKey: 'date',
            header: 'Date',
            cell: (info) => <span className="text-white opacity-75">{info.getValue()}</span>,
        },
        {
            accessorKey: 'candidateName',
            header: 'Candidate Name',
            cell: (info) => <span className="fw-bold text-danger">{info.getValue()}</span>,
        },
        {
            accessorKey: 'roleName',
            header: 'Role Title',
            cell: (info) => <span className="text-white">{info.getValue()}</span>,
        },
        {
            accessorKey: 'auditionType',
            header: 'Audition Type',
            cell: (info) => (
                <Badge bg="info" className="text-uppercase" style={{ fontSize: '10px' }}>
                    {info.getValue()}
                </Badge>
            ),
        },
        {
            accessorKey: 'verifyStatus',
            header: 'Verify Status',
            cell: (info) => <span className="text-white">{info.getValue()}</span>,
        },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: (info) => (
                <Badge bg={info.getValue() === 'Pending' ? 'warning' : 'success'}>
                    {info.getValue()}
                </Badge>
            ),
        },
        {
            id: 'actions',
            header: () => <div className="text-center">Action</div>,
            enableSorting: false,
            cell: (info) => {
                const appId = info.row.original.id;
                return (
                    <div className="d-flex justify-content-center gap-2">
                        <Button variant="link" className="p-0 text-white" onClick={() => handleViewApp(appId)}>
                            <FaEye />
                        </Button>
                    </div>
                );
            },
        }
    ], []);

    // ✅ RENDER MOBILE CARD
    const renderMobileCard = (item) => (
        <Card key={item.id} className="mb-3 app-mobile-card bg-dark border-secondary">
            <Card.Body className="p-3">
                <div className="d-flex justify-content-between mb-2">
                    <span className="fw-bold text-white fs-6">{item.candidateName}</span>
                    <Badge bg={item.status === 'Pending' ? 'warning' : 'success'}>{item.status}</Badge>
                </div>
                
                <div className="small text-white-50 mb-1">
                    Role: <span className="text-white">{item.roleName}</span>
                </div>
                <div className="small text-white-50 mb-3">
                    Project: <span className="text-info">{item.projectName}</span>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="small text-white-50">{item.date !== 'N/A' ? item.date : 'No Date Set'}</span>
                    <Badge bg="info" className="text-uppercase" style={{ fontSize: '9px' }}>
                        {item.auditionType}
                    </Badge>
                </div>

                <div className="d-flex gap-2 border-top border-secondary pt-3 mt-1">
                    <Button size="sm" variant="outline-light" className="w-50 d-flex align-items-center justify-content-center gap-2" onClick={() => handleViewApp(item.id)}>
                        <FaEye /> View
                    </Button>
                    <Button size="sm" variant="outline-danger" className="w-50 d-flex align-items-center justify-content-center gap-2">
                        <FaTrash /> Delete
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );

    // ✅ LOADING STATE
    if (loading) {
        return (
            <div className="text-center p-5 vh-100" style={{ backgroundColor: '#232323' }}>
                <Spinner animation="border" variant="danger" />
                <p className="text-white mt-2">Loading Applications...</p>
            </div>
        );
    }

    return (
        <div className="manage-applications-container p-2 p-md-3 animate-fade-in">
            <MetaTags title="Application List - BookMyAudition" />
            
            <div className="page-header mb-4">
                <h5 className="text-uppercase fw-bold color-b7343a pb-2 m-0 border-bottom border-secondary">
                    Application List
                </h5>
            </div>
            
            {error && <Alert variant="danger" className="bg-dark text-danger border-danger py-2 small">{error}</Alert>}

            {/* ✅ REUSABLE TANSTACK DATA TABLE */}
            <DataTable 
                columns={columns} 
                data={data} 
                renderMobileItem={renderMobileCard} 
                searchPlaceholder="Search talent, role, or project..." 
            />

        </div>
    );
};

export default ManageApplicationList;